/**
 * Eingaben zu einer Flaeche.
 *
 * Was hier steht, steht bewusst hier: die Neigung, die Bauart des Dachs und die
 * Randabstaende. Der Azimut steht daneben, aber als Ergebnis — er kommt aus der
 * Traufkante und wird nur ueberschrieben, wenn der Nutzer widerspricht.
 */

import { useEffect, useRef, useState } from 'react';

import { himmelsrichtung, kantenlaenge } from '@pv/geometrie';

import type { Belegung, Flaeche, Modul, Tragwerkangabe } from '../typen.ts';
import type { Randabstaende } from '@pv/geometrie';

import {
  aendereHindernis,
  hindernislage,
  modulfelderVon,
  type Hindernisaenderung,
  type Planung,
} from '../planung.ts';
import { Zahlfeld } from './Zahlfeld.tsx';
import { leseZahl } from '../zahl.ts';

interface Eigenschaften {
  /**
   * Welcher Arbeitsschritt: die Dachgeometrie oder die Belegung. Vorher stand
   * beides untereinander in einem Reiter — Name, Neigung, Traufe,
   * Referenzmass, Dachtyp, Modul, Raender, Lage, Masse — und wer die Raender
   * suchte, scrollte durch die halbe Dachaufnahme (Rueckmeldung aus dem
   * ersten Test bei Wania: "bessere Menuestrukturen").
   */
  teil: 'dach' | 'module';
  flaeche: Flaeche;
  belegung: Belegung | null;
  module: Modul[];
  planung: Planung | null;
  gemessen: number | null;
  onFlaeche: (felder: Partial<Flaeche>) => void;
  onBelegung: (felder: Partial<Belegung>) => void;
  onTraufkanteWaehlen: () => void;
  onLoeschen: () => void;
  /** Welche Kante zum Einmessen gewaehlt ist — die Karte hebt sie hervor. */
  onReferenzkante: (index: number | null) => void;
  /** Kanten auf die Traufkante einrasten (PV-D21). */
  onBegradigen: () => void;
  /** Welches Hindernis im Panel gerade bearbeitet wird — die Karte hebt es hervor. */
  gewaehltesHindernis: number | null;
  onHindernisGewaehlt: (index: number | null) => void;
  /** Hindernis auf der Karte ziehen, Ecken setzen und entfernen. */
  onHindernisFormen: (index: number) => void;
  /** Ein weiteres Modulfeld auf der Dachfläche zeichnen. */
  onModulfeldZeichnen?: () => void;
  /** Ein gezeichnetes Modulfeld auf der Karte ziehen und formen. */
  onModulfeldFormen?: (index: number) => void;
}

const NEIGUNGEN = [0, 15, 22, 30, 35, 45];

const DACHTYPEN: Array<{ wert: Flaeche['dachtyp']; text: string }> = [
  { wert: 'ziegel', text: 'Ziegel' },
  { wert: 'blech', text: 'Blech' },
  { wert: 'welleternit', text: 'Welleternit' },
  { wert: 'flachdach', text: 'Flachdach' },
  { wert: 'trapezblech', text: 'Trapezblech' },
];

export function Flaechenpanel(e: Eigenschaften): React.JSX.Element {
  const { flaeche, belegung, module, planung } = e;
  const [azimutVonHand, setAzimutVonHand] = useState(false);

  // Feinschieben: Zwei schnelle Tipps auf denselben Pfeil lasen beide den
  // alten Versatz, weil der erste noch nicht gespeichert zurueck war — aus
  // 10 cm wurden 5. Deshalb rechnet der naechste Tipp vom zuletzt
  // angeforderten Stand aus. Kommt ein Stand zurueck, den wir selbst
  // angefordert haben, aendert er nichts; kommt ein anderer (Zuruecksetzen,
  // Feldlage, Abgleich), gilt der.
  const versatz = useRef({ u: belegung?.regeln.versatzU ?? 0, v: belegung?.regeln.versatzV ?? 0 });
  const angefordert = useRef<string[]>([]);
  useEffect(() => {
    const u = belegung?.regeln.versatzU ?? 0;
    const v = belegung?.regeln.versatzV ?? 0;
    const schluessel = `${u}|${v}`;
    const index = angefordert.current.indexOf(schluessel);
    if (index >= 0) {
      angefordert.current = angefordert.current.slice(index + 1);
      return;
    }
    versatz.current = { u, v };
    angefordert.current = [];
  }, [belegung?.regeln.versatzU, belegung?.regeln.versatzV, flaeche.id]);
  // Dasselbe bei den Hindernissen: Wer "Traufe" eintraegt und gleich weiter
  // zu "links" tippt, rechnete links vom Stand vor der Traufe — die Traufe
  // war wieder weg. Solange die Flaeche noch die alte ist, gilt das zuletzt
  // Angeforderte.
  const hindernisseAngefordert = useRef<{ basis: Flaeche['aussparungen']; neu: Flaeche['aussparungen'] } | null>(
    null,
  );
  const aendere = (index: number, aenderung: Hindernisaenderung): void => {
    const offen = hindernisseAngefordert.current;
    const aktuell = offen && offen.basis === flaeche.aussparungen ? offen.neu : flaeche.aussparungen;
    const ring = aendereHindernis({ ...flaeche, aussparungen: aktuell }, index, aenderung);
    if (!ring) return;
    const neu = aktuell.map((alt, i) => (i === index ? ring : alt));
    hindernisseAngefordert.current = { basis: flaeche.aussparungen, neu };
    e.onFlaeche({ aussparungen: neu });
  };
  // Und bei den Regeln: "nach 10 Modulen" eintragen und gleich weiter zum
  // Abstand tippen schrieb den Abstand auf den Stand ohne die 10.
  const regelnAngefordert = useRef<{ basis: Belegung['regeln']; neu: Belegung['regeln'] } | null>(null);
  const aendereRegeln = (aenderung: (aktuell: Belegung['regeln']) => Partial<Belegung['regeln']>): void => {
    if (!belegung) return;
    const offen = regelnAngefordert.current;
    const aktuell = offen && offen.basis === belegung.regeln ? offen.neu : belegung.regeln;
    const neu = { ...aktuell, ...aenderung(aktuell) };
    regelnAngefordert.current = { basis: belegung.regeln, neu };
    e.onBelegung({ regeln: neu });
  };
  const schiebe = (du: number, dv: number): void => {
    if (!belegung) return;
    versatz.current = { u: runde2(versatz.current.u + du), v: runde2(versatz.current.v + dv) };
    angefordert.current.push(`${versatz.current.u}|${versatz.current.v}`);
    // Über die angeforderten Regeln: Wer gleich nach „Lage: Mittig“ einen
    // Pfeil tippte, schob vom Stand ohne die neue Lage aus.
    const { u, v } = versatz.current;
    aendereRegeln(() => ({ versatzU: u, versatzV: v }));
  };
  const [referenzKanteGewaehlt, setReferenzKante] = useState(flaeche.referenzmass?.kanteIndex ?? 0);
  // Nach dem Bearbeiten zieht die gespeicherte Kante mit; die Auswahl hier auch.
  // Und hat die Fläche weniger Ecken als vorher, zeigt sie nie ins Leere.
  useEffect(() => {
    if (flaeche.referenzmass) setReferenzKante(flaeche.referenzmass.kanteIndex);
  }, [flaeche.referenzmass?.kanteIndex]);
  const referenzKante = referenzKanteGewaehlt < flaeche.polygon.length ? referenzKanteGewaehlt : 0;
  const [referenzLaenge, setReferenzLaenge] = useState(
    flaeche.referenzmass ? String(flaeche.referenzmass.laengeM) : '',
  );

  // Die gewaehlte Kante gleich melden — sonst ist beim ersten Blick nicht zu
  // sehen, welche gemeint ist, und man misst die falsche nach.
  useEffect(() => {
    e.onReferenzkante(referenzKante);
    return () => e.onReferenzkante(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenzKante, flaeche.id]);

  // Angezeigt wird, womit gerechnet wird: der Azimut aus dem jetzigen Polygon.
  // Der gespeicherte stammt vom Wählen der Traufe und stimmte nach dem
  // Ziehen einer Ecke nicht mehr mit der Rechnung überein.
  const azimut =
    flaeche.azimut_ueberschrieben ??
    (flaeche.traufkante_index !== null ? (planung?.kennzahlen.azimutGrad ?? flaeche.azimut_berechnet) : null);
  const istFlachdach = flaeche.dachtyp === 'flachdach' || flaeche.neigung_grad === 0;
  const istFassade = flaeche.art === 'fassade';
  const istHochformat = (belegung?.ausrichtung ?? 'hoch') === 'hoch';
  const modulfelder = modulfelderVon(belegung);
  const hatModulfeld = modulfelder.length > 0;
  const trennung = belegung?.regeln.trennung ?? {};
  // Ohne gespeicherte Lage sitzt ein Modulfeld mittig, die ganze Fläche an der Traufe.
  const laengsLage = belegung?.regeln.feldlage?.laengs ?? (hatModulfeld ? 'mitte' : 'traufe');
  const dach = e.teil === 'dach';
  const belegt = e.teil === 'module';

  return (
    <div>
      {dach && (
        <>
      <div className="feld">
        <label htmlFor="flaechenname">Name der Fläche</label>
        <input
          id="flaechenname"
          defaultValue={flaeche.name}
          onBlur={(ereignis) => e.onFlaeche({ name: ereignis.target.value })}
        />
      </div>

      {/* --- Neigung (PV-D12) ------------------------------------------- */}
      <div className="feld">
        <label htmlFor="neigung">Dachneigung in Grad</label>
        <Zahlfeld
          id="neigung"
          min={0}
          max={90}
          stellen={1}
          wert={flaeche.neigung_grad}
          onWert={(grad) => grad !== null && e.onFlaeche({ neigung_grad: grad })}
        />
        <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
          {NEIGUNGEN.map((grad) => (
            <button
              key={grad}
              aria-pressed={flaeche.neigung_grad === grad}
              onClick={() => e.onFlaeche({ neigung_grad: grad })}
            >
              {grad}°
            </button>
          ))}
        </div>
        <p style={{ fontSize: '0.78rem', color: 'var(--grau)', margin: '0.35rem 0 0' }}>
          Aus Prozent: {flaeche.neigung_grad > 0 ? prozentAus(flaeche.neigung_grad) : '0'} %
          Gefälle
        </p>
      </div>

      {/* --- Traufhöhe, für den Schattenwurf -------------------------------- */}
      {!istFassade && (
        <div className="feld">
          <label htmlFor="traufhoehe">Traufhöhe über Grund in m</label>
          <Zahlfeld
            id="traufhoehe"
            min={0}
            max={60}
            stellen={2}
            leerErlaubt
            placeholder="z. B. 6"
            wert={flaeche.hoehe_ueber_grund_m}
            onWert={(m) => e.onFlaeche({ hoehe_ueber_grund_m: m })}
          />
          <p className="klein gedaempft" style={{ margin: '0.3rem 0 0' }}>
            Für den Schattenwurf: Ein 10 m hoher Baum wirft auf ein Dach in 6 m Höhe den Schatten von 4 m.
            Leer heißt, es wird auf den Boden gerechnet — der Schatten fällt dann eher zu lang aus.
          </p>
        </div>
      )}

      {/* --- Traufkante und Azimut (PV-D10, PV-D11) --------------------- */}
      <div className="feld">
        <label>Ausrichtung</label>
        {azimut === null ? (
          <div className="hinweis hinweis--warnung">
            Noch keine Traufkante gewählt. Der Azimut wird daraus berechnet, nicht eingegeben —
            bis dahin sind Module und Ertrag vorläufig und rechnen mit Kante 1 als Traufe.
          </div>
        ) : (
          <p style={{ margin: '0 0 0.5rem' }}>
            {azimut.toFixed(0)}° — {himmelsrichtung(azimut)}
            {flaeche.azimut_ueberschrieben !== null ? ' (von Hand gesetzt)' : ''}
          </p>
        )}
        {flaeche.azimut_ueberschrieben !== null && (
          <div className="hinweis hinweis--warnung">
            Der Azimut ist von Hand gesetzt und gilt vor der Traufkante — eine andere Traufe
            ändert am Ertrag nichts, solange das so steht.
          </div>
        )}
        <div className="knopfreihe">
          <button onClick={e.onTraufkanteWaehlen}>Traufkante auf der Karte wählen</button>
          {flaeche.azimut_ueberschrieben !== null ? (
            <button onClick={() => e.onFlaeche({ azimut_ueberschrieben: null })}>
              Wieder aus der Traufe rechnen
            </button>
          ) : (
            <button className="schlicht" onClick={() => setAzimutVonHand((offen) => !offen)}>
              Azimut von Hand setzen …
            </button>
          )}
        </div>
        {/* Bewusst versteckt: Ein freies Zahlenfeld direkt unter der Ausrichtung
            wurde im ersten Test ueberschrieben, ohne dass klar war, wie gezaehlt
            wird — 15 heisst hier fast Nord, nicht 15 Grad aus Sued. */}
        {(azimutVonHand || flaeche.azimut_ueberschrieben !== null) && (
          <label className="klein" style={{ display: 'block', marginTop: '0.4rem' }}>
            Azimut in Grad — gezählt von Nord im Uhrzeigersinn: 0 Nord, 90 Ost, 180 Süd, 270
            West
            <Zahlfeld
              min={0}
              max={360}
              stellen={1}
              leerErlaubt
              placeholder="z. B. 180 für Süd"
              wert={flaeche.azimut_ueberschrieben}
              onWert={(grad) => e.onFlaeche({ azimut_ueberschrieben: grad === 360 ? 0 : grad })}
            />
          </label>
        )}
      </div>

      {/* --- Referenzmass (PV-D15) -------------------------------------- */}
      <div className="feld">
        <label>Referenzmaß</label>
        <p style={{ fontSize: '0.8rem', color: 'var(--grau)', margin: '0 0 0.4rem' }}>
          Eine Kante mit dem Laser nachmessen — das Polygon skaliert darauf. Danach muss das
          Luftbild nur noch die Form richtig hergeben.
        </p>
        <select
          value={referenzKante}
          onChange={(ereignis) => {
            const index = Number(ereignis.target.value);
            setReferenzKante(index);
            e.onReferenzkante(index);
          }}
        >
          {flaeche.polygon.map((_, index) => (
            <option key={index} value={index}>
              Kante {index + 1}
              {planung ? ` — ${kantenlaenge(planung.grundriss, index).toFixed(2)} m` : ''}
            </option>
          ))}
        </select>
        <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
          <input
            type="number"
            step={0.01}
            min={0}
            placeholder="gemessene Länge in m"
            value={referenzLaenge}
            onChange={(ereignis) => setReferenzLaenge(ereignis.target.value)}
            style={{ width: '10rem' }}
          />
          {e.gemessen !== null && (
            <button onClick={() => setReferenzLaenge(e.gemessen!.toFixed(2))}>
              {e.gemessen.toFixed(2)} m übernehmen
            </button>
          )}
          <button
            className="wichtig"
            disabled={!(Number(referenzLaenge.replace(',', '.')) > 0)}
            onClick={() =>
              e.onFlaeche({
                referenzmass: { kanteIndex: referenzKante, laengeM: Number(referenzLaenge.replace(',', '.')) },
              })
            }
          >
            Skalieren
          </button>
          {flaeche.referenzmass && (
            <button onClick={() => e.onFlaeche({ referenzmass: null })}>Zurücknehmen</button>
          )}
        </div>
        {flaeche.referenzmass && (
          <p style={{ fontSize: '0.78rem', margin: '0.4rem 0 0' }}>
            Eingemessen auf {flaeche.referenzmass.laengeM.toFixed(2)} m an Kante{' '}
            {flaeche.referenzmass.kanteIndex + 1}.
          </p>
        )}
      </div>

      {/* --- Dachtyp (PV-D13, PV-D14) ----------------------------------- */}
      <div className="feld">
        <label htmlFor="dachtyp">Dachtyp und Eindeckung</label>
        <select
          id="dachtyp"
          value={flaeche.dachtyp ?? ''}
          onChange={(ereignis) =>
            e.onFlaeche({ dachtyp: (ereignis.target.value || null) as Flaeche['dachtyp'] })
          }
        >
          <option value="">— wählen —</option>
          {DACHTYPEN.map((typ) => (
            <option key={typ.wert} value={typ.wert ?? ''}>
              {typ.text}
            </option>
          ))}
        </select>
      </div>

      {istFlachdach && (
        <div className="feld">
          <label htmlFor="aufstaenderung">Aufständerung und Sonnenstand</label>
          <div className="knopfreihe">
            <Zahlfeld
              id="aufstaenderung"
              min={0}
              max={60}
              stellen={1}
              leerErlaubt
              placeholder="Aufständerung °"
              wert={flaeche.aufstaenderung_grad}
              onWert={(grad) => e.onFlaeche({ aufstaenderung_grad: grad })}
              style={{ width: '8rem' }}
            />
            <Zahlfeld
              min={5}
              max={80}
              stellen={1}
              leerErlaubt
              placeholder="Sonnenstand °"
              wert={flaeche.sonnenstand_grad}
              onWert={(grad) => e.onFlaeche({ sonnenstand_grad: grad })}
              style={{ width: '8rem' }}
            />
          </div>
          {planung?.raster.reihenabstandM && (
            <p style={{ fontSize: '0.78rem', color: 'var(--grau)', margin: '0.35rem 0 0' }}>
              Reihenabstand {planung.raster.reihenabstandM.toFixed(2)} m — Vorschlag,
              überschreibbar.
            </p>
          )}
        </div>
      )}

      {/* --- Sparren und Pfetten (Mathias, 11/2026) ---------------------- */}
      {/* Das Luftbild zeigt sie nicht — eingetragen wird, was am Dach
          gemessen wurde. Die Haken in der Materialliste setzen sich darauf. */}
      {!istFlachdach && flaeche.art !== 'fassade' && (
        <div className="feld">
          <label>Sparren und Pfetten</label>
          <p className="klein gedaempft" style={{ margin: '0 0 0.4rem' }}>
            Achsabstand und Mitte des ersten Trägers — der Sparren vom linken Rand, vom Boden aus
            gesehen, die Pfette von der Traufe. Die Linien liegen gestrichelt auf dem Dach, die
            Dachhaken der Materialliste sitzen auf ihnen.
          </p>
          <div className="tragwerk">
            {(
              [
                ['sparrenabstandMm', 'Sparrenabstand cm'],
                ['ersterSparrenMm', 'erster Sparren cm'],
                ['pfettenabstandMm', 'Pfettenabstand cm'],
                ['erstePfetteMm', 'erste Pfette cm'],
              ] as const
            ).map(([feld, name]) => {
              const wert = flaeche.tragwerk?.[feld] ?? null;
              return (
                <label key={feld} className="klein">
                  {name}
                  <LeeresZentimeterFeld
                    key={`${feld}-${wert ?? ''}`}
                    mm={wert}
                    onMm={(mm) =>
                      e.onFlaeche({
                        tragwerk: { ...LEERES_TRAGWERK, ...flaeche.tragwerk, [feld]: mm },
                      })
                    }
                  />
                </label>
              );
            })}
          </div>
          {flaeche.tragwerk?.ersterSparrenMm != null && flaeche.tragwerk.sparrenabstandMm && (
            <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
              <span className="klein gedaempft">Sparren schieben</span>
              {[-50, -10, 10, 50].map((schritt) => (
                <button
                  key={schritt}
                  className="schlicht"
                  onClick={() => {
                    const t = flaeche.tragwerk!;
                    // Im Kreis um einen Abstand — weiter links liegt derselbe
                    // Sparren eine Teilung frueher.
                    const abstand = t.sparrenabstandMm!;
                    const neu = ((((t.ersterSparrenMm ?? 0) + schritt) % abstand) + abstand) % abstand;
                    e.onFlaeche({ tragwerk: { ...t, ersterSparrenMm: neu } });
                  }}
                >
                  {schritt > 0 ? '→' : '←'} {Math.abs(schritt / 10)} cm
                </button>
              ))}
            </div>
          )}
        </div>
      )}

        </>
      )}

      {belegt && (
        <>
      {/* --- Modul und Ausrichtung (PV-F01) ----------------------------- */}
      <div className="feld">
        <label htmlFor="modul">Modul</label>
        <select
          id="modul"
          value={belegung?.modul_id ?? ''}
          onChange={(ereignis) => e.onBelegung({ modul_id: ereignis.target.value || null })}
        >
          <option value="">— wählen —</option>
          {module
            .filter((modul) => modul.aktiv || modul.id === belegung?.modul_id)
            .map((modul) => (
              <option key={modul.id} value={modul.id}>
                {modul.hersteller} {modul.typ} — {modul.nennleistung_wp} Wp
                {modul.ist_platzhalter ? ' (Platzhalter)' : ''}
                {!modul.aktiv ? ' (nicht mehr im Pool)' : ''}
              </option>
            ))}
        </select>
        {belegung?.modul_id && module.find((m) => m.id === belegung.modul_id)?.ist_platzhalter && (
          <div className="hinweis">
            Platzhalterwerte aus der Spec. Für ein Angebot muss der echte Modulpool hinterlegt sein.
          </div>
        )}
        <div className="knopfreihe" style={{ marginTop: '0.5rem' }}>
          {(['hoch', 'quer'] as const).map((richtung) => (
            <button
              key={richtung}
              aria-pressed={belegung?.ausrichtung === richtung}
              onClick={() => e.onBelegung({ ausrichtung: richtung })}
            >
              {richtung === 'hoch' ? 'Hochformat' : 'Querformat'}
              {planung?.vergleich ? ` — ${planung.vergleich[richtung]} Module` : ''}
            </button>
          ))}
        </div>
      </div>

      {/* --- Ganze Flaeche oder Modulfeld (Mathias, 11/2026) -------------- */}
      {belegung && !istFassade && (
        <div className="feld">
          <label>Belegen</label>
          <div className="knopfreihe">
            <button
              aria-pressed={!hatModulfeld}
              onClick={() => {
                if (!hatModulfeld) return;
                if (
                  !confirm(
                    modulfelder.length > 1
                      ? `Die ${modulfelder.length} Modulfelder entfernen und die ganze Fläche belegen?`
                      : 'Das gezeichnete Modulfeld entfernen und die ganze Fläche belegen?',
                  )
                )
                  return;
                e.onBelegung({ modulfeld: null });
              }}
            >
              Ganze Fläche
            </button>
            <button
              aria-pressed={hatModulfeld}
              onClick={() => {
                if (!hatModulfeld) e.onModulfeldZeichnen?.();
              }}
            >
              Modulfeld
            </button>
          </div>
          {hatModulfeld ? (
            <>
              <p className="klein gedaempft" style={{ margin: '0.4rem 0' }}>
                Belegt wird nur in den gelb gestrichelten Feldern, jedes für sich mittig. Die
                Randabstände zum Dach und um Hindernisse gelten weiter.
              </p>
              {modulfelder.map((_, index) => (
                <div key={index} className="knopfreihe modulfeldzeile">
                  <span className="klein">Feld {index + 1}</span>
                  <button onClick={() => e.onModulfeldFormen?.(index)}>Verschieben, Ecken ziehen</button>
                  <button
                    className="schlicht gefaehrlich"
                    aria-label={`Modulfeld ${index + 1} entfernen`}
                    onClick={() => {
                      if (!confirm(`Modulfeld ${index + 1} entfernen?`)) return;
                      const rest = modulfelder.filter((__, i) => i !== index);
                      e.onBelegung({ modulfeld: rest.length > 0 ? rest : null });
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className="knopfreihe">
                <button className="schlicht" onClick={() => e.onModulfeldZeichnen?.()}>
                  Weiteres Modulfeld
                </button>
              </div>
              {modulfelder.length > 1 && (
                <p className="klein gedaempft" style={{ margin: '0.2rem 0 0' }}>
                  Entfernt man ein Feld, bekommen die Module der Felder dahinter neue Nummern — Strings
                  über diese Felder bitte danach prüfen.
                </p>
              )}
            </>
          ) : (
            <p className="klein gedaempft" style={{ margin: '0.4rem 0 0' }}>
              „Modulfeld“: ein eigenes Feld auf der Dachfläche zeichnen, mindestens vier Ecken —
              etwa nur der Teil neben der Gaube.
            </p>
          )}
        </div>
      )}

      {/* --- Randabstaende und Fugen (PV-F02, PV-F03) ------------------- */}
      {/* In Zentimetern, so wird auf dem Dach gemessen und gesprochen. Gespeichert
          wird weiter in Millimetern — der Kern und die Materialsaetze rechnen so. */}
      {belegung && (
        <div className="feld">
          <label>Randabstände und Fugen in cm</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            {(
              [
                ['randTraufeMm', 'Traufe'],
                ['randFirstMm', 'First'],
                ['randOrtgangMm', 'Ortgang'],
                ['sicherheitsabstandMm', 'um Hindernisse'],
                ['fugeQuerMm', 'Fuge quer'],
                ['fugeLaengsMm', 'Fuge längs'],
              ] as const
            ).map(([schluessel, text]) => (
              <label key={schluessel} className="klein">
                {text}
                <ZentimeterFeld
                  // Neu aufbauen, wenn der Wert von aussen kommt (Abgleich, andere Flaeche).
                  key={`${flaeche.id}-${schluessel}-${belegung.regeln[schluessel]}`}
                  mm={belegung.regeln[schluessel]}
                  onMm={(mm) => aendereRegeln(() => ({ [schluessel]: mm }))}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {/* --- Trennabstand (Mathias, 11/2026) ----------------------------- */}
      {/* Nach einer Zahl von Modulen statt der Fuge ein groesserer Abstand —
          als Dehnfuge der Schienen oder als Laufweg. Die Schiene endet dort,
          das zaehlt die Materialliste mit. */}
      {belegung && (
        <div className="feld">
          <label>Trennabstand</label>
          <div className="trennung">
            <span className="klein">Nebeneinander nach</span>
            <AnzahlFeld
              key={`qn-${flaeche.id}-${trennung.querNach ?? ''}`}
              wert={trennung.querNach ?? null}
              onWert={(n) => aendereRegeln((r) => ({ trennung: { ...r.trennung, querNach: n } }))}
            />
            <span className="klein">Modulen</span>
            <LeeresZentimeterFeld
              key={`qa-${flaeche.id}-${trennung.querAbstandMm ?? ''}`}
              mm={trennung.querAbstandMm ?? null}
              onMm={(mm) => aendereRegeln((r) => ({ trennung: { ...r.trennung, querAbstandMm: mm } }))}
            />
            <span className="klein">cm</span>

            <span className="klein">Übereinander nach</span>
            <AnzahlFeld
              key={`ln-${flaeche.id}-${trennung.laengsNach ?? ''}`}
              wert={trennung.laengsNach ?? null}
              onWert={(n) => aendereRegeln((r) => ({ trennung: { ...r.trennung, laengsNach: n } }))}
            />
            <span className="klein">Reihen</span>
            <LeeresZentimeterFeld
              key={`la-${flaeche.id}-${trennung.laengsAbstandMm ?? ''}`}
              mm={trennung.laengsAbstandMm ?? null}
              onMm={(mm) => aendereRegeln((r) => ({ trennung: { ...r.trennung, laengsAbstandMm: mm } }))}
            />
            <span className="klein">cm</span>
          </div>
          {((trennung.querNach && !trennung.querAbstandMm) ||
            (!trennung.querNach && trennung.querAbstandMm) ||
            (trennung.laengsNach && !trennung.laengsAbstandMm) ||
            (!trennung.laengsNach && trennung.laengsAbstandMm)) && (
            <p className="klein gedaempft" style={{ margin: '0.3rem 0 0' }}>
              Eine Trennung braucht beides: nach wie vielen Modulen und wie groß.
            </p>
          )}
          {((trennung.querAbstandMm != null &&
            trennung.querAbstandMm > 0 &&
            trennung.querAbstandMm <= belegung.regeln.fugeQuerMm) ||
            (trennung.laengsAbstandMm != null &&
              trennung.laengsAbstandMm > 0 &&
              trennung.laengsAbstandMm <= belegung.regeln.fugeLaengsMm)) && (
            <p className="klein gedaempft" style={{ margin: '0.3rem 0 0' }}>
              Ein Trennabstand bis zur Fuge ändert nichts — er muss größer sein als die Fuge.
            </p>
          )}
        </div>
      )}

        </>
      )}

      {dach && (
        <>
      {/* --- Hindernisse (PV-D07) -------------------------------------- */}
      {/* Vorher gab es nach dem Zeichnen keinen Weg zurueck — keine Liste, kein
          Entfernen. Und das gezeichnete Mass war auf einem Luftbild mit 29 cm
          je Bildpunkt Glueckssache. Jetzt Mass, Drehung und Lage wie mit dem
          Massband: vom Rand bis zur Kante des Hindernisses. */}
      {flaeche.aussparungen.length > 0 && (
        <div className="feld">
          <label>Hindernisse</label>
          <p className="klein gedaempft" style={{ margin: '0 0 0.4rem' }}>
            Maße auf dem Dach: Breite längs der Traufe, Höhe im Gefälle. Abstände vom Rand bis zur
            Kante des Hindernisses, links und rechts vom Boden aus gesehen. Ein eingetragenes Maß
            macht aus der gezeichneten Form ein Rechteck. Um jedes Hindernis bleibt der
            Sicherheitsabstand frei.
          </p>
          {flaeche.aussparungen.map((_, index) => {
            const lage = hindernislage(flaeche, index);
            const setze = (aenderung: Hindernisaenderung): void => aendere(index, aenderung);
            // Der Schluessel haengt am Wert: Aendert sich das Hindernis von
            // aussen (Ziehen auf der Karte, Abgleich), steht der neue Wert im Feld.
            const feld = (name: string, m: number | null, setzeMm: (mm: number) => void): React.JSX.Element => (
              <label className="klein">
                {name}
                {m === null ? (
                  <input value="–" disabled />
                ) : (
                  <ZentimeterFeld key={`${name}-${index}-${m.toFixed(3)}`} mm={Math.round(m * 1000)} onMm={setzeMm} />
                )}
              </label>
            );
            const rand = (name: string, schluessel: keyof Randabstaende): React.JSX.Element =>
              feld(name, lage?.abstaende[schluessel] ?? null, (mm) =>
                setze({ rand: schluessel, abstandM: mm / 1000 }),
              );
            return (
              <div
                key={index}
                className="hindernis"
                aria-current={e.gewaehltesHindernis === index ? 'true' : undefined}
                onFocus={() => e.onHindernisGewaehlt(index)}
                onClick={() => e.onHindernisGewaehlt(index)}
              >
                <div className="hindernis__kopf">
                  <span className="hindernis__name">Hindernis {index + 1}</span>
                  <button
                    className="schlicht"
                    title="Auf der Karte verschieben oder Ecken ziehen"
                    onClick={(ereignis) => {
                      ereignis.stopPropagation();
                      e.onHindernisFormen(index);
                    }}
                  >
                    Verschieben
                  </button>
                  <button
                    className="schlicht gefaehrlich"
                    title={`Hindernis ${index + 1} entfernen`}
                    aria-label={`Hindernis ${index + 1} entfernen`}
                    onClick={(ereignis) => {
                      ereignis.stopPropagation();
                      if (!confirm(`Hindernis ${index + 1} entfernen?`)) return;
                      e.onHindernisGewaehlt(null);
                      // Vom zuletzt angeforderten Stand, wie beim Ändern eines Maßes.
                      const offen = hindernisseAngefordert.current;
                      const aktuell = offen && offen.basis === flaeche.aussparungen ? offen.neu : flaeche.aussparungen;
                      const neu = aktuell.filter((__, i) => i !== index);
                      hindernisseAngefordert.current = { basis: flaeche.aussparungen, neu };
                      e.onFlaeche({ aussparungen: neu });
                    }}
                  >
                    ×
                  </button>
                </div>
                {lage && (
                  <div className="hindernis__felder">
                    {feld('Breite cm', lage.mass.breiteM, (mm) => {
                      if (mm > 0) setze({ breiteM: mm / 1000 });
                    })}
                    {feld('Höhe cm', lage.mass.hoeheM, (mm) => {
                      if (mm > 0) setze({ hoeheM: mm / 1000 });
                    })}
                    <label className="klein">
                      Drehung °
                      <GradFeld
                        key={`d-${index}-${lage.mass.drehungGrad.toFixed(2)}`}
                        grad={lage.mass.drehungGrad}
                        onGrad={(grad) => setze({ drehungGrad: grad })}
                      />
                    </label>
                    {rand('Traufe cm', 'traufeM')}
                    {rand('First cm', 'firstM')}
                    {rand('links cm', 'linksM')}
                    {rand('rechts cm', 'rechtsM')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* --- Groesse des Dachs ----------------------------------------- */}
      {planung && (
        <dl className="zahlenblock">
          <div>
            <dt>Grundfläche</dt>
            <dd>{planung.kennzahlen.projizierteFlaecheM2.toFixed(1)} m²</dd>
          </div>
          <div>
            <dt>Dachfläche</dt>
            <dd>{planung.kennzahlen.wahreFlaecheM2.toFixed(1)} m²</dd>
          </div>
          <div>
            <dt>{istFassade ? 'Wandbreite' : 'Dachbreite'}</dt>
            <dd>{planung.masse.dachBreiteM.toFixed(2)} m</dd>
          </div>
          <div>
            <dt>{istFassade ? 'Wandhöhe' : 'Traufe bis First'}</dt>
            <dd>{planung.masse.dachLaengeM.toFixed(2)} m</dd>
          </div>
        </dl>
      )}
      {planung && planung.winkelabweichungGrad > 0.3 && (
        <div className="hinweis hinweis--warnung">
          Die Fläche ist um <strong>{planung.winkelabweichungGrad.toFixed(1)}°</strong> nicht
          rechtwinklig — das kann eine Modulreihe kosten.
          <div className="knopfreihe" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
            <button onClick={e.onBegradigen}>Auf die Traufkante rechtwinklig machen</button>
          </div>
        </div>
      )}
        </>
      )}

      {belegt && (
        <>
      {/* --- Lage des Feldes ------------------------------------------- */}
      {/* Die Raender sind Mindestwerte. Was das Raster nicht ausfuellt, bleibt
          uebrig — hier steht, wohin dieser Rest geht. Das Raster sucht dabei
          zuerst die Lage mit den meisten Modulen. */}
      {belegung && planung && planung.zahlen.anzahl > 0 && (
        <div className="feld">
          <label>Lage der Module</label>
          <div className="knopfreihe">
            {(
              [
                ['traufe', 'An der Traufe'],
                ['mitte', 'Mittig'],
                ['first', 'Am First'],
              ] as const
            ).map(([wert, text]) => (
              <button
                key={wert}
                aria-pressed={laengsLage === wert}
                onClick={() =>
                  aendereRegeln((r) => ({
                    feldlage: { quer: r.feldlage?.quer ?? 'mitte', laengs: wert },
                    versatzV: 0,
                  }))
                }
              >
                {text}
              </button>
            ))}
          </div>
          <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
            {(
              [
                ['links', 'Links'],
                ['mitte', 'Mittig'],
                ['rechts', 'Rechts'],
              ] as const
            ).map(([wert, text]) => (
              <button
                key={wert}
                aria-pressed={(belegung.regeln.feldlage?.quer ?? 'mitte') === wert}
                onClick={() =>
                  aendereRegeln((r) => ({
                    feldlage: { laengs: r.feldlage?.laengs ?? laengsLage, quer: wert },
                    versatzU: 0,
                  }))
                }
              >
                {text}
              </button>
            ))}
          </div>
          <div className="verschieben">
            <span className="klein">Feinschieben um 5 cm</span>
            <div className="verschieben__pfeile">
              {(
                [
                  ['▲', 'Richtung First', 0, 0.05],
                  ['◀', 'Nach links', -0.05, 0],
                  ['▶', 'Nach rechts', 0.05, 0],
                  ['▼', 'Richtung Traufe', 0, -0.05],
                ] as const
              ).map(([zeichen, titel, du, dv]) => (
                <button
                  key={titel}
                  title={titel}
                  aria-label={titel}
                  onClick={() => schiebe(du, dv)}
                >
                  {zeichen}
                </button>
              ))}
            </div>
            {(belegung.regeln.versatzU || belegung.regeln.versatzV) ? (
              <button
                className="schlicht"
                onClick={() => aendereRegeln(() => ({ versatzU: 0, versatzV: 0 }))}
              >
                Verschoben um {Math.round((belegung.regeln.versatzU ?? 0) * 100)} /{' '}
                {Math.round((belegung.regeln.versatzV ?? 0) * 100)} cm — zurücksetzen
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* --- Kennzahlen (PV-E02, PV-F08) -------------------------------- */}
      {planung && (
        <dl className="zahlenblock">
          <div>
            <dt>Grundfläche</dt>
            <dd>{planung.kennzahlen.projizierteFlaecheM2.toFixed(1)} m²</dd>
          </div>
          <div>
            <dt>Dachfläche</dt>
            <dd>{planung.kennzahlen.wahreFlaecheM2.toFixed(1)} m²</dd>
          </div>
          <div>
            <dt>Module</dt>
            <dd>{planung.zahlen.anzahl}</dd>
          </div>
          <div>
            <dt>Leistung</dt>
            <dd>{planung.zahlen.leistungKwp.toFixed(2)} kWp</dd>
          </div>
          <div>
            <dt>Belegungsgrad</dt>
            <dd>{(planung.zahlen.belegungsgrad * 100).toFixed(0)} %</dd>
          </div>
          <div>
            <dt>Entfernt</dt>
            <dd>{belegung?.entfernte_plaetze.length ?? 0}</dd>
          </div>
        </dl>
      )}

      {/* Herausgenommen wird nur im eigenen Modus "Module an/aus" — vorher reichte
          ein Tipp in der normalen Ansicht, und wer nur die Flaeche waehlen
          wollte, hatte ein Loch im Feld. */}
      {planung && planung.zahlen.anzahl > 0 && (
        <p className="klein" style={{ marginTop: '-0.3rem' }}>
          Einzelne Module herausnehmen — etwa vor einem Dachfenster — geht oben mit „Module
          an/aus“. Herausgenommene Plätze bleiben gestrichelt stehen.
        </p>
      )}

      {/* --- Masse (PV-F08) --------------------------------------------- */}
      {planung && (
        <div className="kachel">
          <h3 style={{ marginTop: 0 }}>Maße in der Dachebene</h3>
          <dl className="masstafel">
            <div>
              <dt>{istFassade ? 'Wandbreite' : 'Dachbreite'}</dt>
              <dd>{planung.masse.dachBreiteM.toFixed(2)} m</dd>
            </div>
            <div>
              <dt>{istFassade ? 'Wandhöhe' : 'Traufe bis First'}</dt>
              <dd>{planung.masse.dachLaengeM.toFixed(2)} m</dd>
            </div>
          </dl>

          {planung.masse.belegtBreiteM === null ? (
            <p className="klein" style={{ margin: '0.5rem 0 0' }}>
              Noch kein Modul gesetzt — die Ränder kommen, sobald belegt ist.
            </p>
          ) : (
            <>
              <dl className="masstafel" style={{ marginTop: '0.5rem' }}>
                <div>
                  <dt>Modulblock</dt>
                  <dd>
                    {planung.masse.belegtBreiteM.toFixed(2)} ×{' '}
                    {planung.masse.belegtLaengeM!.toFixed(2)} m
                  </dd>
                </div>
              </dl>

              {/* Die Raender so anordnen, wie sie auf dem Dach liegen — eine
                  Liste von vier Zahlen muss man sich sonst erst zurechtlegen. */}
              <div className="randkreuz">
                <span />
                <span className="randkreuz__wert">
                  <small>First</small>
                  {planung.masse.randFirstM!.toFixed(2)} m
                </span>
                <span />
                <span className="randkreuz__wert">
                  <small>links</small>
                  {planung.masse.randLinksM!.toFixed(2)} m
                </span>
                <span className="randkreuz__mitte">{planung.zahlen.anzahl} Module</span>
                <span className="randkreuz__wert">
                  <small>rechts</small>
                  {planung.masse.randRechtsM!.toFixed(2)} m
                </span>
                <span />
                <span className="randkreuz__wert">
                  <small>Traufe</small>
                  {planung.masse.randTraufeM!.toFixed(2)} m
                </span>
                <span />
              </div>

              <p className="klein" style={{ margin: '0.5rem 0 0' }}>
                Das ist der Platz, der wirklich frei bleibt — meist mehr als der eingestellte
                Mindestabstand, weil das Raster in der Fläche nicht aufgeht.
              </p>

              {/* Die Frage beim Planen: Randabstand kleiner gesetzt, es bleibt
                  sichtbar Platz, und trotzdem kommt kein Modul dazu. Statt
                  von Hand nachzurechnen, steht die Rechnung jetzt da. */}
              {planung.reserve && (
                <p className="klein" style={{ margin: '0.35rem 0 0' }}>
                  {planung.modulfelder ? 'Im Modulfeld sind frei' : 'Über die Mindestabstände hinaus sind frei'}: in Falllinie{' '}
                  {planung.reserve.reiheFreiM.toFixed(2)} m — eine weitere <strong>Reihe</strong>{' '}
                  bräuchte {planung.reserve.reiheBrauchtM.toFixed(2)} m; quer{' '}
                  {planung.reserve.spalteFreiM.toFixed(2)} m — eine weitere{' '}
                  <strong>Spalte</strong> bräuchte {planung.reserve.spalteBrauchtM.toFixed(2)} m.
                  {planung.vergleich &&
                    planung.vergleich[istHochformat ? 'quer' : 'hoch'] > planung.zahlen.anzahl &&
                    ` Im ${istHochformat ? 'Querformat' : 'Hochformat'} wären es ${planung.vergleich[istHochformat ? 'quer' : 'hoch']} Module.`}
                </p>
              )}

              {/* Schief gezeichnet kostet Module, ohne dass man es sieht:
                  Das Raster läuft längs der Traufe, die anderen Kanten nicht
                  ganz — und oben stößt eine Reihe an. */}
              {planung.winkelabweichungGrad > 0.3 && (
                <div className="hinweis hinweis--warnung" style={{ marginBottom: 0 }}>
                  Die Fläche ist um <strong>{planung.winkelabweichungGrad.toFixed(1)}°</strong> nicht
                  rechtwinklig. Auf {planung.masse.dachBreiteM.toFixed(1)} m Breite sind das{' '}
                  {(
                    planung.masse.dachBreiteM *
                    Math.tan((planung.winkelabweichungGrad * Math.PI) / 180)
                  ).toFixed(2)}{' '}
                  m Versatz — genug, dass eine Modulreihe an die Kante stößt und wegfällt, obwohl
                  Platz auszusehen scheint.
                  <div className="knopfreihe" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                    <button onClick={e.onBegradigen}>Auf die Traufkante rechtwinklig machen</button>
                  </div>
                </div>
              )}

              {/* Bei einem L-foermigen Dach misst "rechts" bis zur Ecke des
                  umschliessenden Rechtecks. Das ist nicht der Platz, der neben
                  den Modulen wirklich frei liegt — also dazusagen, statt eine
                  Zahl hinzustellen, die in die Irre fuehrt. */}
              {planung.masse.huellgenauigkeit < 0.9 && (
                <div className="hinweis hinweis--warnung" style={{ marginBottom: 0 }}>
                  Diese Fläche ist nicht rechteckig (füllt{' '}
                  {(planung.masse.huellgenauigkeit * 100).toFixed(0)} % ihres Hüllrechtecks). Die
                  vier Randmaße sind bis zur Ecke des Hüllrechtecks gemessen und können größer
                  sein als der Platz, der neben den Modulen tatsächlich frei liegt.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {belegung && belegung.entfernte_plaetze.length > 0 && (
        <button
          onClick={() => e.onBelegung({ entfernte_plaetze: [] })}
          title="Setzt alle von Hand entfernten Module wieder — einzeln geht es mit einem Tipp auf den gestrichelten Platz"
        >
          Alle {belegung.entfernte_plaetze.length} entfernten Module zurücksetzen
        </button>
      )}

        </>
      )}
    </div>
  );
}

/** Umrechnung von Grad auf Prozent Gefaelle, als Hilfe (PV-D12). */
function prozentAus(grad: number): string {
  return (Math.tan((grad * Math.PI) / 180) * 100).toFixed(0);
}

const runde2 = (wert: number): number => Math.round(wert * 100) / 100;

/**
 * Ein Feld in Zentimetern, gespeichert in Millimetern.
 *
 * Gespeichert wird erst beim Verlassen oder mit Enter. Vorher ging jeder
 * Tastendruck sofort in die Belegung — wer das Feld leerte, um neu zu tippen,
 * schrieb dabei eine 0 hinein, und das Raster sprang hin und her, waehrend
 * noch getippt wurde.
 */
function ZentimeterFeld({
  mm,
  onMm,
}: {
  mm: number;
  onMm: (mm: number) => void;
}): React.JSX.Element {
  const [text, setText] = useState(formatiereCm(mm));
  const uebernehmen = (): void => {
    const wert = leseZahl(text) ?? NaN;
    if (text.trim() === '' || !Number.isFinite(wert) || wert < 0) {
      setText(formatiereCm(mm));
      return;
    }
    const neu = Math.round(wert * 10);
    if (neu !== mm) onMm(neu);
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      onChange={(ereignis) => setText(ereignis.target.value)}
      onBlur={uebernehmen}
      onKeyDown={(ereignis) => {
        if (ereignis.key === 'Enter') (ereignis.target as HTMLInputElement).blur();
      }}
    />
  );
}

/**
 * Drehung in Grad, wie das Zentimeterfeld erst beim Verlassen übernommen.
 * Zwischen −45 und 45 — ein Rechteck sieht alle 90 Grad gleich aus.
 */
function GradFeld({ grad, onGrad }: { grad: number; onGrad: (grad: number) => void }): React.JSX.Element {
  const anzeige = (g: number): string => g.toLocaleString('de-AT', { maximumFractionDigits: 1, useGrouping: false });
  const [text, setText] = useState(anzeige(grad));
  const uebernehmen = (): void => {
    const wert = leseZahl(text) ?? NaN;
    if (text.trim() === '' || !Number.isFinite(wert) || Math.abs(wert) > 45) {
      setText(anzeige(grad));
      return;
    }
    if (Math.abs(wert - grad) > 0.05) onGrad(wert);
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      onChange={(ereignis) => setText(ereignis.target.value)}
      onBlur={uebernehmen}
      onKeyDown={(ereignis) => {
        if (ereignis.key === 'Enter') (ereignis.target as HTMLInputElement).blur();
      }}
    />
  );
}

const LEERES_TRAGWERK: Tragwerkangabe = {
  sparrenabstandMm: null,
  ersterSparrenMm: null,
  pfettenabstandMm: null,
  erstePfetteMm: null,
};

/**
 * Wie das Zentimeterfeld, aber leer erlaubt: Ein geleertes Feld heißt
 * „nicht eingetragen“, nicht null Zentimeter.
 */
function LeeresZentimeterFeld({
  mm,
  onMm,
}: {
  mm: number | null;
  onMm: (mm: number | null) => void;
}): React.JSX.Element {
  const [text, setText] = useState(mm == null ? '' : formatiereCm(mm));
  const uebernehmen = (): void => {
    if (text.trim() === '') {
      if (mm != null) onMm(null);
      return;
    }
    const wert = leseZahl(text) ?? NaN;
    if (!Number.isFinite(wert) || wert < 0) {
      setText(mm == null ? '' : formatiereCm(mm));
      return;
    }
    const neu = Math.round(wert * 10);
    if (neu !== mm) onMm(neu);
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      placeholder="–"
      onChange={(ereignis) => setText(ereignis.target.value)}
      onBlur={uebernehmen}
      onKeyDown={(ereignis) => {
        if (ereignis.key === 'Enter') (ereignis.target as HTMLInputElement).blur();
      }}
    />
  );
}

/** Eine ganze Zahl ab 1, leer erlaubt: leer heißt „keine Trennung“. */
function AnzahlFeld({
  wert,
  onWert,
}: {
  wert: number | null;
  onWert: (wert: number | null) => void;
}): React.JSX.Element {
  const [text, setText] = useState(wert == null ? '' : String(wert));
  const uebernehmen = (): void => {
    if (text.trim() === '') {
      if (wert != null) onWert(null);
      return;
    }
    const neu = Number(text);
    if (!Number.isInteger(neu) || neu < 1) {
      setText(wert == null ? '' : String(wert));
      return;
    }
    if (neu !== wert) onWert(neu);
  };
  return (
    <input
      type="text"
      inputMode="numeric"
      value={text}
      placeholder="–"
      onChange={(ereignis) => setText(ereignis.target.value)}
      onBlur={uebernehmen}
      onKeyDown={(ereignis) => {
        if (ereignis.key === 'Enter') (ereignis.target as HTMLInputElement).blur();
      }}
    />
  );
}

// Ohne Tausenderpunkt: „1.426“ las das Feld beim Verlassen als 1,426 cm —
// wer nur hineintippte und wieder hinaus, verschob das Hindernis um 14 m
// (iPad-Durchlauf, 11/2026).
const formatiereCm = (mm: number): string =>
  (mm / 10).toLocaleString('de-AT', { maximumFractionDigits: 1, useGrouping: false });

